import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { EditRelation } from '@rossum/api-client/relations';
import { AnnotationStatus, Message } from '@rossum/api-client/shared';
import { Url } from '@rossum/api-client/utils';
import {
  IconAlertTriangle,
  IconBolt,
  IconCustomEInvoicing,
  IconHistory,
  IconLibrary,
  IconMessages,
  IconSchoolOff,
} from '@rossum/ui/icons/tabler';
import { Stack, SvgIcon, Tooltip } from '@rossum/ui/material';
import { GridRenderCellParams } from '@rossum/ui/x-data-grid-pro';
import { startValidation } from '../../../redux/modules/ui/actions';
import {
  AttachmentStatus,
  hasFailedProcessing,
} from '../../../types/attachmentStatus';
import { SupportedAnnotationView } from '../../document-list-base/supportedAnnotationViews';
import { SimpleColumnsLocation } from '../../document-list-base/types';
import {
  attachmentsFeatureSelector,
  duplicateHandlingFeatureSelector,
} from '../../pricing/selectors';
import { AttachmentsIndicator } from './AttachmentsIndicator';
import { DocumentWasEdited } from './DocumentWasEdited';
import { EmailBadge } from './EmailBadge';
import { Note } from './Note';
import { SuggestedEdit } from './SuggestedEdit';

export type RenderDetailsRow = {
  id: number;
  url: string;
  einvoice: boolean;
  automated: boolean;
  automaticallyRejected: boolean;
  status: AnnotationStatus;
  hasAttachments: boolean;
  messages: Message[] | null;
  duplicates: unknown[];
  emailThread: Url | null;
  hasEmailThreadWithReplies?: boolean;
  hasEmailThreadWithNewReplies?: boolean;
  email: Url | null;
  suggestedEdit: Url | null;
  edit?: EditRelation;
  hasNote?: boolean;
  attachmentStatus?: AttachmentStatus;
};

type Props = {
  componentLocation?: SimpleColumnsLocation;
  hasWorkflowRun: boolean;
  /**
   * Kept to exactly what the `excluded_from_training` details filter matches,
   * so the icon never shows on a row the filter would leave out.
   */
  isExcludedFromTraining: boolean;
  handleSelectAnnotation?: (params: {
    annotationUrl: string;
    view: SupportedAnnotationView;
  }) => void;
} & GridRenderCellParams<RenderDetailsRow, string[]>;

export const RenderDetails = ({
  row,
  api,
  hasWorkflowRun,
  isExcludedFromTraining,
  handleSelectAnnotation,
  componentLocation,
}: Props) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  // AUTOMATION
  const showAutomatedIcon = row.automated || row.automaticallyRejected;

  // SUGGESTED EDIT
  const shouldShowSuggestedEditStatuses =
    row.status === 'postponed' ||
    row.status === 'to_review' ||
    row.status === 'reviewing';

  const annotate = (url: string) =>
    dispatch(startValidation(url, api.getAllRowIds().map(Number)));

  // ATTACHMENTS
  const { hasAttachments, messages } = row;

  const isAttachmentsEnabled = useSelector(attachmentsFeatureSelector);

  const isDuplicateFeaturePurchased = useSelector(
    duplicateHandlingFeatureSelector
  );

  return (
    <Stack
      alignItems="center"
      direction="row"
      width="100%"
      height="100%"
      color="text.disabled"
      spacing={1.5}
    >
      {isDuplicateFeaturePurchased &&
        row.duplicates.length > 0 &&
        componentLocation !== 'duplicatesDrawer' && (
          <Stack
            onClick={() =>
              handleSelectAnnotation?.({
                annotationUrl: row.url,
                view: 'duplicates',
              })
            }
            data-cy="details-column-duplicate"
            sx={{ cursor: 'pointer' }}
          >
            <SvgIcon color="warning" fontSize="medium">
              <IconLibrary />
            </SvgIcon>
          </Stack>
        )}
      {hasFailedProcessing(row.attachmentStatus) && (
        <Tooltip
          title={intl.formatMessage({
            id: 'components.unverifiedFileIcon.tooltip',
          })}
          placement="top"
        >
          <Stack data-cy="details-column-unverified-file">
            <SvgIcon color="warning" fontSize="medium">
              <IconAlertTriangle />
            </SvgIcon>
          </Stack>
        </Tooltip>
      )}
      {row.einvoice && (
        <Tooltip
          title={intl.formatMessage({ id: 'component.eInvoice.label' })}
          placement="top"
        >
          <Stack data-cy="details-column-einvoice">
            <SvgIcon fontSize="medium">
              <IconCustomEInvoicing />
            </SvgIcon>
          </Stack>
        </Tooltip>
      )}
      {!!row.emailThread && (
        <EmailBadge
          onClick={() =>
            handleSelectAnnotation?.({
              annotationUrl: row.url,
              view: 'emails',
            })
          }
          hasReplies={row.hasEmailThreadWithReplies ?? false}
          hasNewReplies={row.hasEmailThreadWithNewReplies ?? false}
          dataCy="details-column-email"
        />
      )}
      {hasWorkflowRun && (
        <Stack
          onClick={() =>
            handleSelectAnnotation?.({
              annotationUrl: row.url,
              view: 'activities',
            })
          }
          sx={{ cursor: 'pointer' }}
        >
          <Tooltip
            title={intl.formatMessage({
              id: 'components.workflowActivityDrawer.title',
            })}
            placement="top"
          >
            <SvgIcon fontSize="medium">
              <IconHistory />
            </SvgIcon>
          </Tooltip>
        </Stack>
      )}
      {isAttachmentsEnabled &&
        hasAttachments &&
        componentLocation !== 'attachmentsDrawer' && (
          <AttachmentsIndicator
            onClick={() =>
              handleSelectAnnotation?.({
                annotationUrl: row.url,
                view: 'attachments',
              })
            }
            dataCy="details-column-attachment"
          />
        )}
      {showAutomatedIcon && (
        <Stack sx={{ zIndex: 1 }}>
          <Tooltip
            title={intl.formatMessage({
              id: row.automaticallyRejected
                ? 'components.automaticallyRejectedIcon.tooltip'
                : 'components.automationIcon.tooltip',
            })}
            placement="top"
          >
            <SvgIcon fontSize="medium">
              <IconBolt />
            </SvgIcon>
          </Tooltip>
        </Stack>
      )}
      {shouldShowSuggestedEditStatuses && row.suggestedEdit && (
        <SuggestedEdit
          annotationId={row.id}
          onClick={(e, linkUrl) => {
            if (!e.metaKey) annotate(linkUrl);
          }}
          dataCySuffix="all-documents-dashboard"
        />
      )}
      {row.edit && row.status !== 'importing' && (
        <DocumentWasEdited
          edit={row.edit}
          childAnnotationId={row.id}
          onClick={event => {
            event.stopPropagation();
          }}
          dataCySuffix="all-documents-dashboard"
        />
      )}
      {messages?.length ? <Note messages={messages} /> : null}
      {row.hasNote ? (
        <Stack
          onClick={() =>
            handleSelectAnnotation?.({
              annotationUrl: row.url,
              view: 'comments',
            })
          }
          data-cy="details-column-comments"
          sx={{ cursor: 'pointer' }}
        >
          <Tooltip
            title={intl.formatMessage({
              id: 'components.commentsIcon.tooltip',
            })}
          >
            <SvgIcon>
              <IconMessages />
            </SvgIcon>
          </Tooltip>
        </Stack>
      ) : null}
      {isExcludedFromTraining && (
        <Tooltip
          title={intl.formatMessage({
            id: 'components.documentOverview.trainingStatus.disabled',
          })}
          placement="top"
        >
          <Stack data-cy="details-column-excluded-from-training">
            <SvgIcon fontSize="medium">
              <IconSchoolOff />
            </SvgIcon>
          </Stack>
        </Tooltip>
      )}
    </Stack>
  );
};
